(() => {
  if (window.PhotoCombined) return;
  const states = new WeakMap();
  const enabled = gallery => window.PhotoCatalog.enabled && !!gallery.dataset.preview;
  const controls = (page, selector) => page.querySelector(selector);
  const scrollRoot = gallery => {
    const wrapper = gallery.closest('.page-wrapper');
    return wrapper && ['auto','scroll'].includes(getComputedStyle(wrapper).overflowY) ? wrapper : window;
  };
  const scrollPosition = gallery => {
    const root = scrollRoot(gallery);
    return root === window ? window.scrollY : root.scrollTop;
  };
  const scrollToPosition = (gallery,top) => scrollRoot(gallery).scrollTo({top,behavior:'instant'});
  const restorePosition = (gallery,state,top) => {
    const request = {entry:history.state?.photoCombinedEntry,page:gallery.combinedPage,top:Math.max(0,Number(top)||0)};
    state.restoring = request;
    state.activeEntry = request.entry;
    request.page.querySelector('.photo-combined__navigation').focus({preventScroll:true});
    scrollToPosition(gallery,request.top);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (state.restoring !== request || !gallery.isConnected || !enabled(gallery) || history.state?.photoCombinedEntry !== request.entry || gallery.combinedPage !== request.page) return;
      scrollToPosition(gallery,request.top);
      state.scroll.set(request.entry,scrollPosition(gallery));
      state.restoring = null;
    }));
  };
  const updatePurchasePreview = page => {
    const framed = page.dataset.format === 'framed';
    const purchaseFigure = page.querySelector('.photo-combined__purchase-preview .photo-print__figure');
    if (purchaseFigure) purchaseFigure.hidden = page.dataset.format === 'artist' && !page.artistGeometry;
    const paperPreview = page.querySelector('[data-print-paper-preview]');
    if (paperPreview) {
      const artist = page.dataset.format === 'artist';
      paperPreview.hidden = page.dataset.format !== 'print' && !(artist && page.artistGeometry);
      paperPreview.querySelector('[data-paper-preview-dimensions]').textContent = paperPreview.dataset.paperLabel + ': ' + page.querySelector(artist ? '[data-artist-paper]' : '.photo-print__paper-size').textContent + ' · ' + paperPreview.dataset.imageLabel + ': ' + page.querySelector(artist ? '[data-artist-image]' : '.photo-print__image-size').textContent + '.';
      const note = paperPreview.querySelector('[data-paper-preview-note]');
      if (note) note.textContent = artist ? paperPreview.dataset.artistNote : paperPreview.dataset.printNote;
    }
    window.PhotoPrintOptions?.applyPreviewGeometry(page.querySelector('.photo-combined__frame'), page.querySelector('.photo-combined__mat'), page.querySelector('.photo-combined__mat .photo-print__image'), framed ? page.framedGeometry : page.dataset.format === 'artist' ? page.artistGeometry : page.printGeometry, false, page.querySelector('.photo-combined__window'), page.querySelector('[data-purchase-core]'));
    window.PhotoPrintOptions?.applyFrameFinish(page.querySelector('.photo-combined__frame'), framed ? page.framedGeometry : null, page.querySelector('[data-print-frame]').value);
    page.querySelector('[data-combined-frame-notice]').hidden = !framed;
    if (!framed) {
      page.querySelector('.photo-combined__frame').style.backgroundColor='';
      page.querySelector('.photo-combined__mat').style.backgroundColor='';
      return;
    }
    page.querySelector('.photo-combined__frame').style.backgroundColor = page.querySelector('[data-room-frame]').style.backgroundColor;
    page.querySelector('.photo-combined__mat').style.backgroundColor = page.querySelector('[data-room-mat]').style.backgroundColor;
  };
  const preparePreviewModal = (gallery, page) => {
    const dialog = document.createElement('dialog');
    dialog.className = 'photo-preview-modal';
    const header = document.createElement('div');
    header.className = 'photo-preview-modal__header';
    const heading = document.createElement('h2');
    const close = document.createElement('button');
    close.type = 'button';
    close.textContent = 'Close ×';
    close.setAttribute('aria-label', 'Close enlarged preview');
    const stage = document.createElement('div');
    stage.className = 'photo-preview-modal__stage';
    const caption = document.createElement('p');
    caption.className = 'photo-preview-modal__caption';
    header.append(heading, close);
    dialog.append(header, stage, caption);
    page.append(dialog);
    let opener, savedScroll, savedWindowScroll, copy, ratio;
    const fit = () => {
      if (!dialog.open || !copy || !ratio) return;
      const bounds = stage.getBoundingClientRect();
      copy.style.width = Math.min(bounds.width, bounds.height * ratio) + 'px';
    };
    const open = (kind, button) => {
      if (dialog.open) return;
      const source = page.querySelector(kind === 'room' ? '.photo-print__room-scene' : kind === 'paper' ? '.photo-combined__paper-stage' : '.photo-combined__frame');
      const bounds = source.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return;
      opener = button;
      savedScroll = scrollPosition(gallery);
      savedWindowScroll = {left:window.scrollX, top:window.scrollY};
      ratio = bounds.width / bounds.height;
      copy = source.cloneNode(true);
      copy.querySelectorAll('button,[id]').forEach(node => {
        if (node.matches('button')) node.remove();
        else node.removeAttribute('id');
      });
      copy.classList.add('photo-preview-modal__copy');
      if (kind === 'paper') {
        const viewBox = copy.querySelector('svg').viewBox.baseVal;
        ratio = viewBox.width / viewBox.height;
        copy.style.padding = '0';
      }
      for (const image of copy.querySelectorAll('img')) {
        image.loading = 'eager';
        if (kind !== 'room' || image.matches('[data-room-photo]')) {
          const url = new URL(image.src, location.origin);
          url.searchParams.set('width', Math.min(Number(image.width) || 2400, 2400));
          image.src = url.href;
        }
      }
      stage.replaceChildren(copy);
      const title = page.querySelector('.photo-print__title').textContent;
      heading.textContent = kind === 'room' ? 'Illustrative room preview' : kind === 'paper' ? 'Paper and image size — larger view' : 'Choose your print — larger view';
      dialog.setAttribute('aria-label', title + ' — ' + heading.textContent);
      const size = page.querySelector('.photo-print__size-grid input:checked')?.closest('label')?.textContent.trim().replace(/\s+/g, ' ');
      const format = page.querySelector('.photo-print__format-grid input:checked')?.closest('label')?.textContent.trim();
      const frameSelection = page.dataset.format === 'framed' ? page.querySelector('[data-room-selection]').textContent.split(' · ') : null;
      const frameDetails = frameSelection && size?.includes(frameSelection[0]) ? frameSelection.slice(1).join(' · ') : frameSelection?.join(' · ');
      caption.textContent = kind === 'room' ? page.querySelector('[data-print-room] figcaption').textContent.replace('View larger room preview', '').trim() : [title, format, size, page.querySelector('[data-print-paper-preview]:not([hidden]) [data-paper-preview-dimensions]')?.textContent, page.dataset.format === 'framed' ? frameDetails : page.querySelector('[data-print-paper-choice]')?.selectedOptions[0]?.textContent].filter(Boolean).join(' · ');
      dialog.showModal();
      fit();
      close.focus({preventScroll:true});
    };
    close.addEventListener('click', () => dialog.close(), {signal:gallery.abort.signal});
    dialog.addEventListener('cancel', event => { event.preventDefault(); dialog.close(); }, {signal:gallery.abort.signal});
    let startedOutside = false;
    const outside = event => { const r = dialog.getBoundingClientRect(); return event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom; };
    dialog.addEventListener('pointerdown', event => { startedOutside = event.target === dialog && outside(event); }, {signal:gallery.abort.signal});
    dialog.addEventListener('click', event => { if (startedOutside && event.target === dialog && outside(event)) dialog.close(); startedOutside = false; }, {signal:gallery.abort.signal});
    // The close button is the only focusable control in this read-only modal.
    dialog.addEventListener('keydown', event => { if (event.key === 'Tab') { event.preventDefault(); close.focus(); } }, {signal:gallery.abort.signal});
    dialog.addEventListener('close', () => {
      // A queued close event from the preceding view must not clear a new view.
      if (dialog.open) return;
      stage.replaceChildren();
      copy = null;
      opener?.focus({preventScroll:true});
      scrollToPosition(gallery, savedScroll);
      window.scrollTo({...savedWindowScroll, behavior:'instant'});
      requestAnimationFrame(() => { if (!dialog.open && page.isConnected) { scrollToPosition(gallery, savedScroll); window.scrollTo({...savedWindowScroll, behavior:'instant'}); } });
    }, {signal:gallery.abort.signal});
    const observer = new ResizeObserver(fit);
    observer.observe(stage);
    gallery.abort.signal.addEventListener('abort', () => { observer.disconnect(); if (dialog.open) dialog.close(); }, {once:true});
    const addOpeners = (source, kind, label, captionParent) => {
      const imageButton = document.createElement('button');
      imageButton.type = 'button';
      imageButton.className = 'photo-preview-modal__image-open';
      imageButton.setAttribute('aria-label', label);
      imageButton.setAttribute('aria-haspopup', 'dialog');
      imageButton.addEventListener('click', () => open(kind, imageButton), {signal:gallery.abort.signal});
      source.append(imageButton);
      const textButton = document.createElement('button');
      textButton.type = 'button';
      textButton.className = 'photo-preview-modal__text-open';
      textButton.textContent = label;
      textButton.setAttribute('aria-haspopup', 'dialog');
      textButton.addEventListener('click', () => open(kind, textButton), {signal:gallery.abort.signal});
      captionParent.prepend(textButton);
    };
    const figure = page.querySelector('.photo-combined__purchase-preview .photo-print__figure');
    const printCaption = document.createElement('figcaption');
    figure.append(printCaption);
    addOpeners(figure.querySelector('.photo-combined__frame'), 'print', 'View larger print preview', printCaption);
    const room = page.querySelector('[data-print-room]');
    addOpeners(room.querySelector('.photo-print__room-scene'), 'room', 'View larger room preview', room.querySelector('figcaption'));
    const paper = page.querySelector('[data-print-paper-preview]');
    addOpeners(paper.querySelector('.photo-combined__paper-stage'), 'paper', 'View larger paper and image preview', paper.querySelector('figcaption'));
  };
  const updateNavigation = (gallery, state) => {
    const page = gallery.combinedPage;
    if (!page) return;
    const index = state.sequence?.findIndex(photo => photo.handle === page.dataset.photo) ?? -1;
    for (const button of page.querySelectorAll('[data-combined-step]')) {
      const next = index + Number(button.dataset.combinedStep);
      button.disabled = gallery.reviewExpired() || !state.sequence || index < 0 || next < 0 || next >= state.sequence.length;
    }
    const position = controls(page,'[data-combined-position]');
    if (!state.sequence) position.textContent = state.error ? gallery.dataset.navigationErrorLabel : gallery.dataset.loadingLabel;
    else position.textContent = index < 0 ? page.dataset.combinedOutside : gallery.dataset.positionLabel.replace('[current]',index+1).replace('[total]',state.sequence.length);
  };
  const prepareSequence = async (gallery, state, template) => {
    const tools = gallery.querySelector('photo-buyer-tools');
    if (tools && !tools.filtersReady) await new Promise(resolve => gallery.addEventListener('photo-buyer:filters-ready',resolve,{once:true,signal:gallery.abort.signal}));
    if (!gallery.isConnected || gallery.reviewExpired()) return;
    try {
      if (new URLSearchParams(location.search).get('review_return') === 'home') {
        const response = await fetch(template.dataset.homeSequence,{signal:gallery.abort.signal,credentials:'same-origin'});
        if (!response.ok) throw new Error('Home sequence unavailable');
        const home = await response.json();
        if (home.schemaVersion !== 1 || !Array.isArray(home.productIds)) throw new Error('Home sequence changed');
        // The gallery review owns the draft expiry. Home ordering is independently
        // maintained and need not carry an identical generation/expiry timestamp.
        const photos = new Map(gallery.review.photos.map(photo => [photo.id,photo]));
        state.sequence = home.productIds.map(id => photos.get(id));
        if (!window.PhotoCatalog.review) state.sequence = state.sequence.filter(Boolean);
        if (state.sequence.some(photo => !photo)) throw new Error('Home photograph unavailable');
      } else {
        const matched = tools?.matchedRecords || [];
        const photos = new Map(gallery.review.photos.map(photo => [photo.handle,photo]));
        state.sequence = matched.map(button => photos.get(button.dataset.photo)).filter(Boolean);
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      state.sequence = null;
      state.error = true;
    }
    updateNavigation(gallery,state);
  };
  const show = (gallery,page) => {
    if (!enabled(gallery) || !page || gallery.reviewExpired()) return false;
    let state = states.get(gallery);
    if (!state) {
      state = {scroll:new Map(),previousScrollRestoration:history.scrollRestoration};
      states.set(gallery,state);
      const entry = () => 'combined-' + Date.now() + '-' + Math.random().toString(36).slice(2);
      history.replaceState({...history.state,photoCombinedEntry:history.state?.photoCombinedEntry || entry()},'',location.href);
      state.activeEntry = history.state.photoCombinedEntry;
      history.scrollRestoration = 'manual';
      const recordScroll = () => {
        if (enabled(gallery) && state.activeEntry && !state.restoring) state.scroll.set(state.activeEntry,scrollPosition(gallery));
      };
      window.addEventListener('scroll',recordScroll,{passive:true,signal:gallery.abort.signal});
      document.addEventListener('scroll',recordScroll,{capture:true,passive:true,signal:gallery.abort.signal});
      for (const name of ['wheel','touchmove']) window.addEventListener(name,() => {state.restoring=null;},{passive:true,signal:gallery.abort.signal});
      window.addEventListener('pagehide',() => {
        if (!state.restoring) state.scroll.set(state.activeEntry,scrollPosition(gallery));
        state.restoring=null;
        history.scrollRestoration=state.previousScrollRestoration;
      },{signal:gallery.abort.signal});
      window.addEventListener('pageshow',event => {
        if (!event.persisted || !enabled(gallery)) return;
        history.scrollRestoration='manual';
        restorePosition(gallery,state,state.scroll.get(history.state?.photoCombinedEntry) ?? (Number(history.state?.photoCombinedScroll)||0));
      },{signal:gallery.abort.signal});
      gallery.addEventListener('keydown',event => {
        if (!['ArrowLeft','ArrowRight'].includes(event.key) || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        if (!event.target.closest('.photo-combined__art,.photo-combined__navigation') || event.target.closest('input,textarea,select,[contenteditable],.photo-viewer')) return;
        const button = gallery.combinedPage?.querySelector('[data-combined-step="' + (event.key === 'ArrowLeft' ? '-1' : '1') + '"]');
        if (!button || button.disabled) return;
        event.preventDefault();
        button.click();
      },{signal:gallery.abort.signal});
      gallery.addEventListener('click',event => {
        const step = event.target.closest('[data-combined-step]');
        if (step && !step.disabled) {
          event.preventDefault();
          const index = state.sequence?.findIndex(photo => photo.handle === gallery.combinedPage?.dataset.photo) ?? -1;
          const photo = index >= 0 ? state.sequence?.[index + Number(step.dataset.combinedStep)] : null;
          if (!photo || gallery.reviewExpired()) return;
          if (!state.restoring) state.scroll.set(state.activeEntry,scrollPosition(gallery));
          history.replaceState({...history.state,photoCombinedScroll:scrollPosition(gallery)},'',location.href);
          const url = new URL(location.href);
          url.searchParams.set('review_print',photo.handle);
          url.hash = '';
          history.pushState({...history.state,photoCombinedEntry:entry(),photoCombinedScroll:0},'',url.pathname+url.search);
          gallery.showRequestedPrint();
          restorePosition(gallery,state,0);
        }
        const opener = event.target.closest('[data-combined-viewer-open]');
        if (opener && !gallery.reviewExpired()) {
          if (opener.classList.contains('photo-print__image-open') && event.detail > 0 && event.target !== opener.querySelector('img')) return;
          gallery.combinedPage.querySelector('photo-print-viewer').open(opener);
        }
        const purchase = event.target.closest('[data-combined-purchase]');
        if (purchase && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
          event.preventDefault();
          if (gallery.reviewExpired()) return;
          state.restoring=null;
          const heading = gallery.combinedPage.querySelector('[data-combined-options-heading]');
          heading.focus({preventScroll:true});
          heading.scrollIntoView({block:'start',behavior:'instant'});
        }
      },{signal:gallery.abort.signal});
      window.addEventListener('popstate',() => {
        if (!enabled(gallery) || !new URLSearchParams(location.search).has('review_print')) {
          state.restoring=null;
          history.scrollRestoration=state.previousScrollRestoration;
          return;
        }
        if (!state.restoring) state.scroll.set(state.activeEntry,scrollPosition(gallery));
        const top = state.scroll.get(history.state?.photoCombinedEntry) ?? (Number(history.state?.photoCombinedScroll)||0);
        history.scrollRestoration='manual';
        gallery.showRequestedPrint();
        restorePosition(gallery,state,top);
      },{signal:gallery.abort.signal});
    }
    const template = page.querySelector('[data-photo-combined]');
    if (!page.dataset.combinedReady) {
      page.dataset.combinedReady = 'true';
      page.classList.add('photo-combined');
      page.dataset.combinedOutside = template.dataset.outside;
      const fragment = template.content.cloneNode(true);
      const layout = page.querySelector('.photo-print__layout');
      const preview = layout.firstElementChild;
      preview.classList.add('photo-combined__purchase-preview');
      preview.querySelector('[data-paper-preview-stage]').append(page.querySelector('[data-print-diagram]'));
      page.querySelector('.photo-print__diagram-note').remove();
      const viewer = preview.querySelector('photo-print-viewer');
      const figure = viewer.querySelector('.photo-print__figure');
      const upperFigure = figure.cloneNode(true);
      upperFigure.classList.add('photo-combined__art-figure');
      for (const button of upperFigure.querySelectorAll('[data-viewer-open]')) {
        button.removeAttribute('data-viewer-open');
        button.dataset.combinedViewerOpen='';
      }
      const lowerImage = figure.querySelector('.photo-print__image');
      lowerImage.loading='lazy';
      const frame = document.createElement('div');
      frame.className='photo-combined__frame';
      const mat = document.createElement('div');
      mat.className='photo-combined__mat';
      const opening = document.createElement('div');
      opening.className = 'photo-combined__window';
      opening.append(lowerImage);
      mat.append(opening);
      const core = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      core.dataset.purchaseCore = '';
      core.setAttribute('aria-hidden', 'true');
      core.setAttribute('focusable', 'false');
      core.style.display = 'none';
      mat.append(core);
      frame.append(mat);
      figure.replaceChildren(frame);
      const art = document.createElement('div');
      art.classList.add('photo-combined__art');
      const metadata = fragment.querySelector('.photo-combined__metadata');
      metadata.append(page.querySelector('.photo-print__title'),page.querySelector('.photo-print__description'));
      const choices = page.querySelector('.photo-print__choices');
      const lowerTitle = document.createElement('h2');
      lowerTitle.className='photo-print__title';
      lowerTitle.textContent=metadata.querySelector('.photo-print__title').textContent;
      choices.prepend(lowerTitle,metadata.querySelector('.photo-print__description').cloneNode(true));
      const heading = fragment.querySelector('[data-combined-options-heading]');
      heading.id = 'PrintOptions-' + gallery.dataset.sectionId + '-' + page.dataset.photo;
      choices.setAttribute('aria-labelledby',heading.id);
      fragment.querySelector('[data-combined-purchase]').href = '#' + heading.id;
      const share = fragment.querySelector('[data-photo-share-field] input');
      share.id += '-combined-' + page.dataset.photo;
      fragment.querySelector('[data-photo-share-field] label').htmlFor = share.id;
      const navigation = fragment.querySelector('.photo-combined__navigation');
      navigation.tabIndex=-1;
      navigation.setAttribute('aria-label',lowerTitle.textContent+' — '+navigation.getAttribute('aria-label'));
      layout.before(navigation);
      fragment.querySelector('[data-combined-artwork]').append(upperFigure);
      art.append(fragment.querySelector('.photo-combined__stage'),metadata,fragment.querySelector('.photo-combined__actions'));
      const options = document.createElement('div');
      options.className = 'photo-combined__options';
      const purchaseLayout = document.createElement('div');
      purchaseLayout.className='photo-print__layout photo-combined__purchase-layout';
      purchaseLayout.append(preview,choices);
      options.append(heading,purchaseLayout);
      viewer.append(fragment.querySelector('[data-combined-frame-notice]'));
      layout.append(art,options);
      page.addEventListener('change',() => updatePurchasePreview(page),{signal:gallery.abort.signal});
      page.querySelector('.photo-print__review').remove();
      const browse = gallery.contextUrl();
      for (const name of ['find','orientation','color','view_favorites','review_collection']) browse.searchParams.delete(name);
      browse.hash = 'photographs';
      fragment.querySelector('[data-combined-browse]').href = browse.pathname+browse.search+browse.hash;
      page.append(fragment.querySelector('.photo-combined__compare'));
      const image = upperFigure.querySelector('.photo-print__image');
      image.loading='eager';
      image.sizes = '(min-width:1320px) 1056px, calc(100vw - 144px)';
      updatePurchasePreview(page);
      preparePreviewModal(gallery, page);
    }
    for (const sibling of gallery.querySelector('.photo-print-pages').children) sibling.hidden = sibling !== page;
    const bio = gallery.querySelector('.photo-gallery__bio-link');
    if (bio) bio.hidden = true;
    gallery.combinedPage = page;
    gallery.currentPhoto = [...gallery.querySelectorAll('.photo-gallery__grid .photo-gallery__open')].find(button => button.dataset.photo === page.dataset.photo);
    window.PhotoBuyer?.refreshActions(gallery);
    document.title = page.querySelector('.photo-print__title').textContent + ' — The Other Perspective';
    if (!state.preparing) {
      state.preparing = true;
      prepareSequence(gallery,state,template);
    }
    updateNavigation(gallery,state);
    return true;
  };
  window.PhotoCombined = {show,updatePurchasePreview};
  for (const gallery of document.querySelectorAll('photo-gallery[data-preview]')) {
    const page = gallery.querySelector('.photo-print:not([hidden])');
    if (page) show(gallery,page);
  }
})();
